import { InfoCircleOutlined } from '@ant-design/icons';
import { VALID_RANGE_TIME } from '@components/internal-crm/reports/settlements/date-range-filter';
import { DATE_FORMAT, DATE_FORMAT_TIME } from '@constants/index.constant';
import { ExportTypeEnum } from '@enums/export-file.enum';
import { diffTime, UnitEnum } from '@utils/date';
import { DatePicker, Tooltip } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import debounce from 'lodash.debounce';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

import s from './styles.module.scss';

dayjs.extend(customParseFormat);

const { RangePicker } = DatePicker;

const SECOND_PER_DAY = 24 * 60 * 60;
export const VALID_RECORD_DATE = 30;
export const VALID_RECORD_TIME = VALID_RECORD_DATE * SECOND_PER_DAY;
export const VALID_RANGE_30_DAYS = 30 * SECOND_PER_DAY;

export const DateRangeFilter: React.FC<{
    dateRange: [string, string];
    setDateRange: (dateRange: [string, string]) => void;
    showTime?: boolean;
    allowClear?: boolean;
    reportType?: ExportTypeEnum;
}> = ({ dateRange, setDateRange, showTime = false, allowClear = false, reportType }) => {
    const { t } = useTranslation('common');
    const [error, setError] = useState<string>();

    const ranges: any = reportType
        ? {
              [t('yesterday')]: [
                  dayjs().add(-1, 'd').startOf('d'),
                  dayjs().add(-1, 'd').endOf('d'),
              ],
              [t('today')]: [dayjs().startOf('d'), dayjs().endOf('d')],
              [t('last_7_days')]: [dayjs().add(-7, 'd').startOf('d'), dayjs().endOf('d')],
              [t('last_14_days')]: [dayjs().add(-14, 'd').startOf('d'), dayjs().endOf('d')],
              [t('last_30_days')]: [dayjs().add(-29, 'd').startOf('d'), dayjs().endOf('d')],
          }
        : {
              [t('yesterday')]: [
                  dayjs().add(-1, 'd').startOf('d'),
                  dayjs().add(-1, 'd').endOf('d'),
              ],
              [t('today')]: [dayjs().startOf('d'), dayjs().endOf('d')],
              [t('last_7_days')]: [dayjs().add(-7, 'd').startOf('d'), dayjs().endOf('d')],
              [t('last_14_days')]: [dayjs().add(-14, 'd').startOf('d'), dayjs().endOf('d')],
              [t('last_30_days')]: [dayjs().add(-29, 'd').startOf('d'), dayjs().endOf('d')],
              [t('this_week')]: [dayjs().startOf('week'), dayjs().endOf('d')],
              [t('this_month')]: [dayjs().startOf('month'), dayjs().endOf('d')],
              [t('this_year')]: [dayjs().startOf('year'), dayjs().endOf('d')],
          };

    const setDateRangeValue = useCallback(
        debounce(
            (range: [string, string]) => {
                const format = showTime ? DATE_FORMAT_TIME : DATE_FORMAT;
                const startDate = dayjs(range[0], format);
                const endDate = dayjs(range[1], format);
                const isValid = isValidRange([startDate, endDate]);
                if (!isValid) return;

                if (showTime) {
                    setDateRange([startDate.toISOString(), endDate.toISOString()]);
                } else {
                    setDateRange([
                        startDate.startOf('d').toISOString(),
                        endDate.endOf('d').toISOString(),
                    ]);
                }
            },
            500,
            { leading: true },
        ),
        [showTime, error],
    );

    const isDisableDate = (date: Dayjs): boolean => {
        if (reportType) {
            let disable =
                +date > +dayjs().endOf('d') ||
                diffTime([date, dayjs()], UnitEnum.SECOND) > VALID_RECORD_TIME;
            if (reportType === ExportTypeEnum.DISPUTES) {
                disable = date.isAfter(dayjs(), 'day');
            }
            return disable;
        }
        return false;
    };

    const isValidRange = (dates: Dayjs[]): boolean => {
        if (reportType) {
            const time = diffTime([dates[0], dates[1]], UnitEnum.SECOND);
            const maxRangeTime =
                reportType === ExportTypeEnum.DISPUTES ? VALID_RANGE_30_DAYS : VALID_RANGE_TIME;

            if (time > maxRangeTime) {
                setError(
                    reportType === ExportTypeEnum.DISPUTES
                        ? t('disputes.tooltip_date_range', { ns: 'common' })
                        : t('history_report.tooltip_date_range'),
                );
                return false;
            }

            setError(undefined);
        }
        return true;
    };

    return (
        <section className="filter">
            <div className="flex mb-2">
                <h3 className="mr-2">{t('filter_by_range', { ns: 'common' })}</h3>
                <Tooltip
                    title={
                        reportType === ExportTypeEnum.DISPUTES
                            ? t('disputes.tooltip_date_range', { ns: 'common' })
                            : t('history_report.tooltip_date_range')
                    }
                >
                    <InfoCircleOutlined />
                </Tooltip>
            </div>
            <div className={`${error ? s.errorRange : s.filterRangeDate} custom-date-range`}>
                <RangePicker
                    className={`h-[42px] select-range-date`}
                    ranges={ranges}
                    defaultValue={dateRange && [dayjs(dateRange[0]), dayjs(dateRange[1])]}
                    disabledDate={(date) => isDisableDate(date)}
                    format={showTime ? DATE_FORMAT_TIME : DATE_FORMAT}
                    onChange={(dates: null | (Dayjs | null)[], dateString: [string, string]) => {
                        if (dates) {
                            setDateRangeValue(dateString);
                        }
                    }}
                    getPopupContainer={(trigger) => trigger}
                    showTime={showTime}
                    allowClear={allowClear}
                />
                {error && <p>{error}</p>}
            </div>
        </section>
    );
};
